import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import BrandLoadingScreen from '../components/BrandLoadingScreen';

// Returns the correct cover image, respecting cover_image_index set by admin
function getCoverImage(house) {
  if (!house.images || house.images.length === 0) return null;
  const idx = house.cover_image_index ?? 0;
  return house.images[idx] || house.images[0];
}

function HouseStatusBadge({ house }) {
  const status = house.status;
  const isVerified = house.is_verified;

  if (status === 'verified' || isVerified === true) {
    return (
      <span className="absolute top-4 right-4 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
        Verified
      </span>
    );
  }
  if (status === 'sample') {
    return <span className="absolute top-4 right-4 bg-violet-500 text-white text-xs font-bold px-3 py-1 rounded-full">Sample</span>;
  }
  return <span className="absolute top-4 right-4 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full">Pending</span>;
}

export default function BrowsePage() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const searchParams = new URLSearchParams(routerLocation.search);

  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [location, setLocation] = useState(searchParams.get('location') || '');
  const [minRent, setMinRent] = useState(searchParams.get('minRent') || searchParams.get('budgetMin') || '');
  const [maxRent, setMaxRent] = useState(searchParams.get('maxRent') || '');
  const [bedrooms, setBedrooms] = useState(searchParams.get('bedrooms') || '');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const limit = 20;

  const adminToken = localStorage.getItem('adminAuthToken');
  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
  const isAdminLoggedIn = !!adminToken && adminUser.role;

  const getDashboardLink = () => isAdminLoggedIn ? '/admin' : '/dashboard';
  const getDashboardLabel = () => isAdminLoggedIn ? 'Admin Panel' : (user?.name ? `Hi, ${user.name.split(' ')[0]}` : 'Dashboard');

  const handleLogout = () => {
    localStorage.removeItem('adminAuthToken');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    logout();
    navigate('/', { replace: true });
    setTimeout(() => window.location.reload(), 100);
  };

  useEffect(() => { setIsMenuOpen(false); }, [routerLocation.pathname]);

  useEffect(() => {
    const params = new URLSearchParams(routerLocation.search);
    setLocation(params.get('location') || '');
    setMinRent(params.get('minRent') || params.get('budgetMin') || '');
    setMaxRent(params.get('maxRent') || '');
    setBedrooms(params.get('bedrooms') || '');
    setPage(Number(params.get('page') || 1));
  }, [routerLocation.search]);

  const fetchHouses = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page, limit });
      if (location) params.append('location', location);
      if (minRent)  params.append('minRent', minRent);
      if (maxRent)  params.append('maxRent', maxRent);
      if (bedrooms) params.append('bedrooms', bedrooms);
      if (isAdminLoggedIn) params.append('status', 'all');
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/houses?${params.toString()}`);
      setHouses(res.data.houses || []);
      setTotalPages(res.data.pages || 1);
    } catch (err) {
      setError('Failed to load houses. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHouses(); }, [page, location, minRent, maxRent, bedrooms]);

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (location) params.set('location', location);
    if (minRent) params.set('minRent', minRent);
    if (maxRent) params.set('maxRent', maxRent);
    if (bedrooms) params.set('bedrooms', bedrooms);
    params.set('page', '1');
    navigate(`/browse?${params.toString()}`);
  };

  const handleViewDetails = (houseId) => {
    if (!user && !isAdminLoggedIn) {
      navigate('/login', { state: { from: `/house/${houseId}` } });
    } else {
      navigate(`/house/${houseId}`);
    }
  };

  const clearFilters = () => {
    navigate('/browse');
  };

  const hasActiveFilters = location || minRent || maxRent || bedrooms;

  return (
    <>
      <SEOHead
        title={location ? `${location} Homes in Ibadan | LiteRent` : 'Browse Homes in Ibadan | LiteRent'}
        description={location ? `Browse LiteRent homes around ${location}, Ibadan and see how eligible renters can move in with 30% upfront and spread the balance over time.` : 'Browse LiteRent homes in Ibadan and see how eligible renters can move in with 30% upfront and spread the balance over time.'}
        canonical={`https://literent.com.ng/browse${routerLocation.search || ''}`}
      />
    <main className="min-h-screen bg-[#fdfdfd] text-[#1a1a1a] flex flex-col">

      {/* ══ HEADER ══ */}
      <header className="sticky top-0 z-50 bg-white border-b border-[#afc09e] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex-shrink-0">
              <img
                src="/Logo.jpg"
                alt="LiteRent Logo"
                className="h-12 sm:h-14 md:h-16 w-auto object-contain"
                onError={(e) => { e.target.src = 'https://via.placeholder.com/180x60?text=LiteRent'; }}
              />
            </Link>

            <nav className="hidden lg:flex items-center space-x-8 xl:space-x-10">
              <Link to="/" className="text-[#294c79] hover:text-[#1f3a5c] font-medium transition-colors">Home</Link>
              <Link to="/#how-it-works" className="text-[#294c79] hover:text-[#1f3a5c] font-medium transition-colors">How It Works</Link>
              <Link to="/browse" className="text-[#a5c250] font-semibold">Browse Houses</Link>
              <Link to="/about" className="text-[#294c79] hover:text-[#1f3a5c] font-medium transition-colors">About Us</Link>
              <Link to="/contact" className="text-[#294c79] hover:text-[#1f3a5c] font-medium transition-colors">Contact Us</Link>
              <div className="flex items-center space-x-4">
                {(user || isAdminLoggedIn) ? (
                  <>
                    <Link to={getDashboardLink()}>
                      <button className={`px-6 py-2.5 text-white rounded-full font-medium transition shadow-sm ${isAdminLoggedIn ? 'bg-red-600 hover:bg-red-700' : 'bg-[#294c79] hover:bg-[#1f3a5c]'}`}>
                        {getDashboardLabel()}
                      </button>
                    </Link>
                    <button onClick={handleLogout} className="px-6 py-2.5 border-2 border-red-500 text-red-600 rounded-full font-medium hover:bg-red-50 transition">
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login"><button className="px-6 py-2.5 border-2 border-[#294c79] text-[#294c79] rounded-full font-medium hover:bg-[#294c79]/10 transition">Login</button></Link>
                    <Link to="/signup"><button className="px-6 py-2.5 bg-[#a5c250] text-white rounded-full font-medium hover:bg-[#8bb83a] shadow-sm transition">Get Started</button></Link>
                  </>
                )}
              </div>
            </nav>

            <button
              className="lg:hidden p-2 rounded-md text-[#294c79] hover:bg-gray-100 focus:outline-none"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMenuOpen
                  ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
              </svg>
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="lg:hidden bg-white border-t border-[#afc09e] py-6 px-5">
            <div className="flex flex-col space-y-5">
              <Link to="/" className="text-[#294c79] font-medium py-3 text-lg" onClick={() => setIsMenuOpen(false)}>Home</Link>
              <Link to="/#how-it-works" className="text-[#294c79] font-medium py-3 text-lg" onClick={() => setIsMenuOpen(false)}>How It Works</Link>
              <Link to="/browse" className="text-[#a5c250] font-semibold py-3 text-lg" onClick={() => setIsMenuOpen(false)}>Browse Houses</Link>
              <Link to="/about" className="text-[#294c79] font-medium py-3 text-lg" onClick={() => setIsMenuOpen(false)}>About Us</Link>
              <Link to="/contact" className="text-[#294c79] font-medium py-3 text-lg" onClick={() => setIsMenuOpen(false)}>Contact Us</Link>
              <div className="pt-6 border-t border-gray-200 flex flex-col space-y-4">
                {(user || isAdminLoggedIn) ? (
                  <>
                    <Link to={getDashboardLink()} onClick={() => setIsMenuOpen(false)}>
                      <button className={`w-full py-4 text-white rounded-full font-medium text-lg ${isAdminLoggedIn ? 'bg-red-600' : 'bg-[#294c79]'}`}>{getDashboardLabel()}</button>
                    </Link>
                    <button onClick={() => { handleLogout(); setIsMenuOpen(false); }} className="w-full py-4 border-2 border-red-500 text-red-600 rounded-full font-medium text-lg">Logout</button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setIsMenuOpen(false)}><button className="w-full py-4 border-2 border-[#294c79] text-[#294c79] rounded-full font-medium text-lg">Login</button></Link>
                    <Link to="/signup" onClick={() => setIsMenuOpen(false)}><button className="w-full py-4 bg-[#a5c250] text-white rounded-full font-medium text-lg">Get Started</button></Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* ══ PAGE HERO BANNER ══ */}
      <section className="bg-[#294c79] py-12 sm:py-16 px-5 sm:px-8 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-64 h-64 bg-[#a5c250]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-12 -left-12 w-48 h-48 bg-white/5 rounded-full blur-2xl pointer-events-none" />
        <div className="max-w-7xl mx-auto relative z-10">
          <span className="inline-block bg-[#a5c250]/20 border border-[#a5c250]/30 text-[#c8e87a] text-xs font-semibold px-4 py-2 rounded-full tracking-widest uppercase mb-4">
            Ibadan, Nigeria
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 leading-tight">
            Browse Verified Homes
          </h1>
          <p className="text-white/70 text-lg max-w-xl">
            Every listing is physically inspected. Move in with just 30% upfront — pay the rest over 11 months.
          </p>
        </div>
      </section>


      {/* ══ FILTERS ══ */}
      <section className="bg-[#f5f9ee] border-b border-[#e8f2d1] py-8 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto">
          <form onSubmit={handleFilterSubmit}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-5">
              <div>
                <label className="block text-xs font-semibold text-[#294c79] mb-2 uppercase tracking-wide">Location</label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="e.g. Akobo, Challenge, Ring Road"
                  className="w-full px-4 py-3 border border-[#afc09e] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#a5c250] text-base"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#294c79] mb-2 uppercase tracking-wide">Min Rent (₦/yr)</label>
                <input
                  type="number"
                  value={minRent}
                  onChange={(e) => setMinRent(e.target.value)}
                  placeholder="e.g. 500,000"
                  className="w-full px-4 py-3 border border-[#afc09e] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#a5c250] text-base"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#294c79] mb-2 uppercase tracking-wide">Max Rent (₦/yr)</label>
                <input
                  type="number"
                  value={maxRent}
                  onChange={(e) => setMaxRent(e.target.value)}
                  placeholder="e.g. 3,000,000"
                  className="w-full px-4 py-3 border border-[#afc09e] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#a5c250] text-base"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#294c79] mb-2 uppercase tracking-wide">Bedrooms</label>
                <select
                  value={bedrooms}
                  onChange={(e) => setBedrooms(e.target.value)}
                  className="w-full px-4 py-3 border border-[#afc09e] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#a5c250] text-base"
                >
                  <option value="">Any</option>
                  <option value="1">1 Bedroom</option>
                  <option value="2">2 Bedrooms</option>
                  <option value="3">3 Bedrooms</option>
                  <option value="4">4+ Bedrooms</option>
                </select>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="submit"
                className="flex-1 bg-[#294c79] text-white py-3.5 rounded-xl font-semibold hover:bg-[#1f3a5c] transition text-base"
              >
                Search Houses
              </button>
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="sm:w-40 border-2 border-[#294c79] text-[#294c79] py-3.5 rounded-xl font-medium hover:bg-[#294c79]/10 transition text-base"
                >
                  Clear Filters
                </button>
              )}
            </div>
          </form>
        </div>
      </section>

      {/* ══ RESULTS ══ */}
      <section className="flex-1 py-10 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto">

          {loading && (
            <BrandLoadingScreen fullScreen={false} message="Loading homes that match your search…" />
          )}

          {error && !loading && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-5 rounded-2xl text-center text-lg">
              {error}
              <button onClick={fetchHouses} className="ml-4 underline font-medium">Try again</button>
            </div>
          )}

          {!loading && !error && (
            <>
              {houses.length > 0 && (
                <p className="text-[#1a1a1a]/50 text-sm mb-6">
                  Showing <strong className="text-[#294c79]">{houses.length}</strong> {houses.length === 1 ? 'home' : 'homes'}
                  {hasActiveFilters ? ' matching your filters' : ' in Ibadan'}
                </p>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
                {houses.length === 0 ? (
                  <div className="col-span-full text-center py-24">
                    <div className="text-6xl mb-5">🏠</div>
                    <p className="text-2xl font-semibold text-[#294c79] mb-3">No homes found</p>
                    <p className="text-[#1a1a1a]/60 mb-6">Try adjusting your filters or clearing them to see all available homes.</p>
                    {hasActiveFilters && (
                      <button onClick={clearFilters} className="bg-[#294c79] text-white px-8 py-3 rounded-full font-medium hover:bg-[#1f3a5c] transition">
                        Clear Filters
                      </button>
                    )}
                  </div>
                ) : (
                  houses.map((house) => (
                    <div
                      key={house._id || house.id}
                      className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-[#e8f2d1] group flex flex-col"
                    >
                      {/* Image — uses getCoverImage to respect admin-set cover */}
                      <div className="relative h-52 bg-gray-100 flex-shrink-0">
                        {house.images && house.images.length > 0 ? (
                          <img
                            src={getCoverImage(house)}
                            alt={house.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-300">
                            <svg className="w-12 h-12 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                            </svg>
                            <span className="text-sm">No Photo</span>
                          </div>
                        )}
                        <HouseStatusBadge house={house} />
                      </div>

                      {/* Body */}
                      <div className="p-5 flex flex-col flex-1">
                        <h3 className="text-lg font-semibold text-[#294c79] mb-1 group-hover:text-[#1f3a5c] transition line-clamp-1">
                          {house.title || 'Untitled House'}
                        </h3>
                        <p className="text-[#1a1a1a]/60 text-sm mb-3 flex items-center gap-1">
                          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {house.location} &nbsp;·&nbsp; {house.bedrooms} Bed{house.bedrooms !== 1 ? 's' : ''}
                        </p>

                        <div className="mt-auto pt-3 border-t border-[#f0f0f0]">
                          <p className="text-xl font-bold text-[#294c79] mb-1">
                            ₦{house.annual_rent ? parseFloat(house.annual_rent).toLocaleString() : '---'}
                            <span className="text-sm font-normal text-[#1a1a1a]/50"> / year</span>
                          </p>
                          <p className="text-xs text-[#a5c250] font-medium mb-4">
                            Move in from ₦{house.annual_rent ? Math.ceil(parseFloat(house.annual_rent) * 0.3).toLocaleString() : '---'} upfront
                          </p>
                          <button
                            onClick={() => handleViewDetails(house._id || house.id)}
                            className="w-full bg-[#294c79] text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-[#1f3a5c] transition"
                          >
                            View Details →
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-4 mt-4 mb-8">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-6 py-3 bg-[#294c79] text-white rounded-xl disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#1f3a5c] transition font-medium"
                  >
                    ← Previous
                  </button>
                  <span className="text-base font-medium text-[#294c79]">Page {page} of {totalPages}</span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-6 py-3 bg-[#294c79] text-white rounded-xl disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#1f3a5c] transition font-medium"
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

     <Footer />
    </main>
    </>
  );
}